import { FormEvent, useEffect, useMemo, useState } from 'react';
import { useMultiStyleConfig } from '@chakra-ui/react';
import FocusLock from 'react-focus-lock';
import Badge from '../../Badge/Badge';
import Box from '../../Box/Box';
import Button from '../../Button/Button';
import ButtonGroup from '../../ButtonGroup/ButtonGroup';
import Checkbox from '../../Form/Checkbox/Checkbox';
import CheckboxGroup from '../../Form/Checkbox/CheckboxGroup';
import Icon from '../../Icon/Icon';
import Radio from '../../Form/Radio/Radio';
import RadioGroup from '../../Form/Radio/RadioGroup';
import SearchInput from '../../SearchInput/SearchInput';
import Text from '../../Text/Text';
import { FilterStyle } from '../Filter.theme';
import { FilterOptions, FilterOptionsMap, FilterValue } from '../Filter.types';
import { isEqual, useDebounce } from '../../../utils/utils';
import { getOptionLabel } from '../Filter.utils';
import { useFilterContext } from '../Filter.context';

export type FilterFormProps = {
  category: string;
  categoryName?: string;
  onChange: (category: string, selected: FilterValue, previousValue: FilterValue) => void;
  onCancel: () => void;
};

const FilterForm = (props: FilterFormProps) => {
  const { category, categoryName, onCancel, onChange } = props;

  const filterStyle = useMultiStyleConfig('Filter') as FilterStyle;

  const { data, state } = useFilterContext();
  const {
    iconsMap,
    hasNotFilteredOption = true,
    preserveOptionOrder,
    isInitialLoading,
    isMultiple,
    onAsyncSearch,
    options,
    optionsMap,
  } = data[category];
  const value = state[category] || [];

  const [selected, setSelected] = useState<FilterValue>(value);

  const [searchValue, setSearchValue] = useState<string>('');
  const debouncedSearchValue = useDebounce<string>(searchValue, 1000);

  const [isLoading, setLoading] = useState(Boolean(isInitialLoading));
  const [foundOptions, setFoundOptions] = useState<FilterOptions>([]);
  const [foundOptionsMap, setFoundOptionsMap] = useState<FilterOptionsMap>();

  const isAsync = !!onAsyncSearch;
  const withSearch = (options && options.length > 5) || isAsync;

  const isDisabled = isEqual(selected, value);

  const filteredOptions = useMemo(() => {
    if (options?.length) {
      return options.filter((opt) => {
        const optLabel = (optionsMap && optionsMap[opt]) || opt;
        return optLabel.toLowerCase().includes(searchValue.toLowerCase());
      });
    }
    return [];
  }, [searchValue, options]);

  const onSearchChange = (q: string) => {
    setSearchValue(q);
    if (isAsync) {
      if (q.length > 0) {
        setLoading(true);
      } else {
        setFoundOptions([]);
      }
    }
  };

  const onSubmit = (e: FormEvent<HTMLDivElement>) => {
    e.preventDefault();
    const newSelected = selected[0] === '' ? [] : selected;
    onChange(category, newSelected, value);
  };

  const onClearClick = () => {
    setSelected([]);
    onChange(category, [], value);
  };

  const onCancelClick = () => {
    setSelected(value);
    onCancel();
  };

  const getEmptyText = () => {
    if (searchValue.length) {
      return 'No result. Refine your search term.';
    }
    return '';
  };

  const getAsyncList = async () => {
    if (onAsyncSearch) {
      const response = await onAsyncSearch(category, searchValue);
      setLoading(false);
      setFoundOptions(response.options);
      setFoundOptionsMap(response.optionsMap || optionsMap);
    }
  };

  useEffect(() => {
    setLoading(Boolean(isInitialLoading));
  }, [isInitialLoading]);

  useEffect(() => {
    if (debouncedSearchValue.length > 0) {
      getAsyncList();
    } else {
      setLoading(Boolean(isInitialLoading));
    }
  }, [debouncedSearchValue]);

  const isEditMode = value.length !== 0;

  const isAsyncSearch = isAsync && !!searchValue;

  const items = isAsyncSearch
    ? Array.from(new Set(preserveOptionOrder ? [...foundOptions] : [...value, ...foundOptions]))
    : Array.from(new Set(preserveOptionOrder ? [...filteredOptions] : [...value, ...filteredOptions]));

  const currentOptionMap = isAsyncSearch ? foundOptionsMap : optionsMap;

  return (
    <FocusLock>
      <Box as="form" data-testid={`${category}-form`} onSubmit={onSubmit} sx={filterStyle.form}>
        <Box sx={filterStyle.formHeader}>
          <Text as="h5" sx={filterStyle.formTitle}>
            {categoryName || category}
          </Text>
          {isMultiple && (
            <Badge variant="subtle" colorScheme="neutral" sx={filterStyle.formBadge}>
              {selected[0] === '' ? '0' : String(selected.length)}
            </Badge>
          )}
        </Box>
        {(withSearch || isAsync) && (
          <SearchInput
            isDisabled={isInitialLoading}
            onChange={onSearchChange}
            placeholder={isAsync ? 'Start typing to search options' : 'Start typing to find options'}
            sx={filterStyle.formSearch}
            value={searchValue}
          />
        )}
        {isLoading && 'Loading...'}
        {!isLoading && isMultiple && (
          <CheckboxGroup onChange={setSelected} sx={filterStyle.formInputGroup} value={selected}>
            {items.length
              ? items.map((opt) => (
                  <Checkbox key={opt} value={opt}>
                    {iconsMap && iconsMap[opt] && <Icon name={iconsMap[opt]} />}
                    {getOptionLabel(opt, currentOptionMap)}
                  </Checkbox>
                ))
              : getEmptyText()}
          </CheckboxGroup>
        )}
        {!isLoading && !isMultiple && (
          <RadioGroup onChange={(v) => setSelected([v])} sx={filterStyle.formInputGroup} value={selected[0] || ''}>
            {hasNotFilteredOption && (
              <Radio value="">
                <Text as="span" color="neutral.40" fontStyle="italic">
                  Not filtered
                </Text>
              </Radio>
            )}
            {items.length
              ? items.map((opt) => {
                  const hasIcon = iconsMap && iconsMap[opt];
                  const label = getOptionLabel(opt, currentOptionMap);
                  return (
                    <Radio key={opt} value={opt}>
                      {hasIcon ? (
                        <Box as="span" display="flex" gap="4">
                          <Icon name={iconsMap[opt]} />
                          {label}
                        </Box>
                      ) : (
                        label
                      )}
                    </Radio>
                  );
                })
              : getEmptyText()}
          </RadioGroup>
        )}
        <ButtonGroup spacing="12" sx={filterStyle.formButtonGroup}>
          {isEditMode && hasNotFilteredOption && (
            <Button marginInlineEnd="auto" onClick={onClearClick} size="sm" variant="tertiary">
              Clear
            </Button>
          )}
          <Button onClick={onCancelClick} size="sm" type="reset" variant="secondary">
            Cancel
          </Button>
          <Button isDisabled={isDisabled} size="sm" type="submit">
            {(selected.length === 0 || selected[0] === '') && isEditMode ? 'Remove' : 'Apply'}
          </Button>
        </ButtonGroup>
      </Box>
    </FocusLock>
  );
};

export default FilterForm;
